import { useRef, useState, useEffect } from "react";
import { FaXTwitter } from "react-icons/fa6";
import { FaTelegramPlane, FaDiscord } from "react-icons/fa";
import { ExternalLink } from "lucide-react";

// Custom hook for intersection observer (iOS-safe)
const useIsVisible = (ref, options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (!ref.current) return;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (options.once !== false) {
          observer.disconnect();
        }
      }
    }, { threshold: options.amount || 0.3 });
    
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, options.once, options.amount]);
  
  return isVisible;
};

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const footerRef = useRef(null);
  const isVisible = useIsVisible(footerRef, { once: true, amount: 0.1 });
  
  const footerLinks = {
    Platform: [
      { name: "Launch App", href: "https://refundyoursol.com", external: true },
      { name: "Staking", href: "https://refundyoursol.com/staking", external: true },
      { name: "Portfolio", href: "https://portfolio.refundyoursol.com", external: true },
    ],
    Community: [
      { name: "Twitter / X", href: "https://x.com/RefundYourSOL", external: true },
      { name: "Telegram News", href: "https://t.me/refundyoursol", external: true },
      { name: "Telegram Chat", href: "https://t.me/refundyoursolcom", external: true },
      { name: "Discord", href: "https://discord.gg/8RPqhgyjXt", external: true },
    ],
    Resources: [
      { name: "API Docs", href: "https://refundyoursol.com/docs", external: true },
      { name: "Buy $RYS", href: "https://swap.pump.fun/?output=8gHPxqgHj6JQ2sQtMSghQYVN5qRP8wm5T6HNejuwpump", external: true },
      { name: "Token Stats", href: "#stats", external: false },
      { name: "Roadmap", href: "#roadmap", external: false },
    ],
  };

  const socialLinks = [
    { icon: FaXTwitter, href: "https://x.com/RefundYourSOL", label: "Twitter" },
    { icon: FaTelegramPlane, href: "https://t.me/refundyoursol", label: "Telegram" },
    { icon: FaDiscord, href: "https://discord.gg/8RPqhgyjXt", label: "Discord" },
  ];

  return (
    <footer ref={footerRef} className="relative py-16 bg-gradient-to-b from-[#0b0b0f] via-black to-black border-t border-white/10 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(168,85,247,0.05),transparent_50%)]" />
      
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          {/* Brand Section */}
          <div className="md:col-span-1">
            <div
              className={`transition-all duration-600 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
              }`}
              style={{ WebkitBackfaceVisibility: 'hidden' }}
            >
              <div className="flex items-center gap-2.5 mb-4">
                <img 
                  src="/project/logo192.png" 
                  alt="RYS Logo" 
                  className="w-10 h-10"
                  loading="lazy"
                />
                <div className="flex flex-col">
                  <span className="text-lg font-bold gradient-text">RYS</span>
                  <span className="text-[10px] text-gray-500">RefundYourSOL</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Powering Solana with Next-Gen DeFi Utilities
              </p>
              {/* Social Icons */}
              <div className="flex gap-3">
                {socialLinks.map((social, i) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-gray-400 hover:text-purple-400 transition-all duration-300 transform-gpu hover:scale-110 hover:-translate-y-0.5 active:scale-95 ${
                        isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                      }`}
                      style={{ 
                        transitionDelay: `${i * 0.1}s`,
                        WebkitBackfaceVisibility: 'hidden',
                      }}
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Links Sections */}
          {Object.entries(footerLinks).map(([category, links], categoryIndex) => (
            <div
              key={category}
              className={`transition-all duration-600 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
              }`}
              style={{ 
                transitionDelay: `${categoryIndex * 0.1}s`,
                WebkitBackfaceVisibility: 'hidden',
              }}
            >
              <h4 className="text-white font-semibold mb-4 text-sm">{category}</h4>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      target={link.external ? "_blank" : undefined}
                      rel={link.external ? "noopener noreferrer" : undefined}
                      className="text-gray-400 hover:text-purple-400 text-sm transition-colors flex items-center gap-1.5 group"
                    >
                      {link.name}
                      {link.external && (
                        <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div
          className={`pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 transition-opacity duration-600 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: '0.3s', WebkitBackfaceVisibility: 'hidden' }}
        >
          <p className="text-gray-500 text-sm">
            © {currentYear} RefundYourSOL. All rights reserved.
          </p>
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <span>Built on</span>
            <span className="text-pink-400 font-semibold">Solana</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
